import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MapPin, Phone, Mail, Instagram, Facebook, Send, CheckCircle, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Contacto = () => {
  const { toast } = useToast();
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    sobrenome: '',
    email: '',
    telefone: '',
    tipoInteresse: '',
    mensagem: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const API = `${BACKEND_URL}/api`;
      
      await axios.post(`${API}/contact`, formData);
      
      setSuccess(true);
      
      toast({
        title: "✅ Mensagem Enviada com Sucesso!",
        description: "Entraremos em contacto consigo em breve.",
      });
      
      setFormData({
        nome: '',
        sobrenome: '',
        email: '',
        telefone: '',
        tipoInteresse: '',
        mensagem: ''
      });
      
      // Reset success state after 5 seconds
      setTimeout(() => setSuccess(false), 5000);
      
    } catch (error) {
      toast({
        title: "Erro ao Enviar",
        description: "Ocorreu um erro. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-[#1a1a1a] pt-20">
      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0 bg-gradient-to-b from-[#242424] to-[#1a1a1a]" />
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 z-10 text-center">
          <p className="text-[#B8860B] text-sm tracking-[0.3em] uppercase mb-4">Fale Connosco</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-semibold text-[#E8DDCB] mb-6" style={{fontFamily: "'Playfair Display', serif"}}>
            Contacto
          </h1>
          <div className="w-16 h-[2px] bg-[#B8860B] mx-auto" />
        </div>
      </section>

      {/* Formulário e Informações */}
      <section className="py-20 bg-[#1a1a1a]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Formulário */}
            <div className="bg-[#242424] rounded-xl p-8 border border-[#B8860B]/10">
              <h2 className="text-2xl font-semibold text-[#E8DDCB] mb-8" style={{fontFamily: "'Playfair Display', serif"}}>
                Envie uma Mensagem
              </h2>
              
              {/* Success Message */}
              {success && (
                <div className="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-lg flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                  <div>
                    <p className="text-green-400 font-semibold">Mensagem enviada com sucesso!</p>
                    <p className="text-green-400/70 text-sm">Entraremos em contacto consigo em breve.</p>
                  </div>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="nome" className="text-[#E8DDCB]/80 text-sm">Nome</Label>
                    <Input
                      id="nome"
                      value={formData.nome}
                      onChange={(e) => handleChange('nome', e.target.value)}
                      required
                      className="bg-[#1a1a1a] border-[#B8860B]/20 text-[#E8DDCB] focus:border-[#B8860B]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sobrenome" className="text-[#E8DDCB]/80 text-sm">Sobrenome</Label>
                    <Input
                      id="sobrenome"
                      value={formData.sobrenome}
                      onChange={(e) => handleChange('sobrenome', e.target.value)}
                      required
                      className="bg-[#1a1a1a] border-[#B8860B]/20 text-[#E8DDCB] focus:border-[#B8860B]"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#E8DDCB]/80 text-sm">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    required
                    className="bg-[#1a1a1a] border-[#B8860B]/20 text-[#E8DDCB] focus:border-[#B8860B]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="telefone" className="text-[#E8DDCB]/80 text-sm">
                    Telefone <span className="text-[#B8860B]">(opcional)</span>
                  </Label>
                  <Input
                    id="telefone"
                    type="tel"
                    value={formData.telefone}
                    onChange={(e) => handleChange('telefone', e.target.value)}
                    className="bg-[#1a1a1a] border-[#B8860B]/20 text-[#E8DDCB] focus:border-[#B8860B]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tipoInteresse" className="text-[#E8DDCB]/80 text-sm">Tipo de Interesse</Label>
                  <Select
                    value={formData.tipoInteresse}
                    onValueChange={(value) => handleChange('tipoInteresse', value)}
                  >
                    <SelectTrigger className="bg-[#1a1a1a] border-[#B8860B]/20 text-[#E8DDCB]">
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#242424] border-[#B8860B]/20 text-[#E8DDCB]">
                      <SelectItem value="pedido">Pedido de Orçamento</SelectItem>
                      <SelectItem value="parceiro">Tornar-me Parceiro</SelectItem>
                      <SelectItem value="evento">Eventos & Catering</SelectItem>
                      <SelectItem value="informacoes">Informações Gerais</SelectItem>
                      <SelectItem value="outro">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mensagem" className="text-[#E8DDCB]/80 text-sm">Mensagem</Label>
                  <Textarea
                    id="mensagem"
                    value={formData.mensagem}
                    onChange={(e) => handleChange('mensagem', e.target.value)}
                    required
                    rows={5}
                    className="bg-[#1a1a1a] border-[#B8860B]/20 text-[#E8DDCB] focus:border-[#B8860B] resize-none"
                  />
                </div>

                <Button 
                  type="submit"
                  size="lg"
                  disabled={sending}
                  data-testid="contact-submit-btn"
                  className="w-full bg-[#B8860B] hover:bg-[#D4A84B] text-[#1a1a1a] font-semibold tracking-wider uppercase text-sm py-6 disabled:opacity-50"
                >
                  {sending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      A Enviar...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Enviar
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Informações de Contacto */}
            <div className="space-y-8">
              <div className="bg-[#242424] rounded-xl p-8 border border-[#B8860B]/10">
                <h3 className="text-xl font-semibold text-[#E8DDCB] mb-6" style={{fontFamily: "'Playfair Display', serif"}}>
                  Informações
                </h3>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 rounded-full bg-[#B8860B]/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-[#B8860B]" />
                    </div>
                    <div>
                      <p className="text-[#E8DDCB] font-medium mb-1">Morada</p>
                      <p className="text-[#E8DDCB]/60 text-sm">
                        Rua das laranjeiras, 14<br />
                        Mafra - PT 2640-577
                      </p>
                    </div>
                  </div>
                  <a href="tel:+351918288122" className="flex items-start space-x-4 hover:opacity-80 transition-opacity" data-testid="contact-phone-link">
                    <div className="w-10 h-10 rounded-full bg-[#B8860B]/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-[#B8860B]" />
                    </div>
                    <div>
                      <p className="text-[#E8DDCB] font-medium mb-1">Telefone</p>
                      <p className="text-[#E8DDCB]/60 text-sm">+351 918 288 122</p>
                    </div>
                  </a>
                  <a href="mailto:geralsantacarne@gmail.com" className="flex items-start space-x-4 hover:opacity-80 transition-opacity" data-testid="contact-email-link">
                    <div className="w-10 h-10 rounded-full bg-[#B8860B]/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-[#B8860B]" />
                    </div>
                    <div>
                      <p className="text-[#E8DDCB] font-medium mb-1">E-mail</p>
                      <p className="text-[#E8DDCB]/60 text-sm">geralsantacarne@gmail.com</p>
                    </div>
                  </a>
                </div>
              </div>

              <div className="bg-[#242424] rounded-xl p-8 border border-[#B8860B]/10">
                <h3 className="text-xl font-semibold text-[#E8DDCB] mb-4" style={{fontFamily: "'Playfair Display', serif"}}>
                  Redes Sociais
                </h3>
                <p className="text-[#E8DDCB]/60 text-sm mb-6">
                  Acompanhe as nossas novidades.
                </p>
                <div className="flex space-x-4">
                  <a
                    href="https://www.instagram.com/santacarnebbq/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full bg-[#B8860B]/10 flex items-center justify-center text-[#B8860B] hover:bg-[#B8860B] hover:text-[#1a1a1a] transition-all"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                  <a
                    href="https://www.facebook.com/santacarnebbq/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full bg-[#B8860B]/10 flex items-center justify-center text-[#B8860B] hover:bg-[#B8860B] hover:text-[#1a1a1a] transition-all"
                  >
                    <Facebook className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contacto;
